/**
 * NEXORA AI - Welcome Step (Premium Refactor)
 * Epic 1.3 Phase 2.2 - Onboarding Premium
 * 
 * Émotion cible: BIENVENUE
 * Standards:
 * - Framer Motion animations (entrance, no inline CSS)
 * - Touch targets: 44x44px minimum
 * - Typography: H1 responsive, H2 text-base md:text-lg
 * - 100% Tailwind CSS
 * - i18n complète
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, ArrowRight, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';

export default function WelcomeStep({ onNext }) {
  const { t } = useTranslation(['onboarding', 'common']);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: 'easeOut' }
    }
  };

  const globeVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, ease: 'easeOut' }
    },
    float: {
      y: [0, -10, 0],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut'
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 sm:p-6 relative overflow-hidden">
      {/* Background effects - Subtle, not distracting */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.4 }}
          transition={{ duration: 1 }}
          className="absolute top-20 left-20 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl"
        />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.3 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="absolute bottom-20 right-20 w-96 h-96 bg-purple-600/15 rounded-full blur-3xl"
        />
      </div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="relative z-10 text-center max-w-3xl w-full"
      >
        {/* Animated Globe Icon - Floating effect for visual interest */}
        <motion.div
          variants={globeVariants}
          animate="float"
          className="mb-8 sm:mb-10 inline-block"
        >
          <div className="w-24 h-24 sm:w-32 sm:h-32 mx-auto rounded-full bg-gradient-to-br from-blue-600 via-blue-500 to-purple-600 flex items-center justify-center shadow-2xl shadow-blue-500/30">
            <Globe className="w-12 h-12 sm:w-16 sm:h-16 text-white" strokeWidth={1.5} />
          </div>
        </motion.div>

        {/* Welcome Title - H1 Premium Typography */}
        <motion.h1
          variants={itemVariants}
          className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold tracking-tight text-white mb-4 sm:mb-5"
        >
          {t('onboarding:welcome_title')}
        </motion.h1>
        
        {/* Subtitle - H2 Responsive as per PO guidelines */}
        <motion.p
          variants={itemVariants}
          className="text-base md:text-lg text-zinc-300 mb-3 sm:mb-4 max-w-2xl mx-auto leading-relaxed"
        >
          {t('onboarding:welcome_subtitle')}
        </motion.p>
        
        {/* Description - Supporting text */}
        <motion.p
          variants={itemVariants}
          className="text-sm sm:text-base text-zinc-400 mb-10 sm:mb-12 max-w-xl mx-auto"
        >
          {t('onboarding:welcome_description')}
        </motion.p>

        {/* Value Proposition with icon - Emotional hook */}
        <motion.div
          variants={itemVariants}
          className="flex items-center justify-center gap-2 mb-8 sm:mb-10 text-sm sm:text-base text-blue-400"
        >
          <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
          <span className="font-medium">{t('onboarding:welcome_value_prop')}</span>
        </motion.div>

        {/* CTA Button - Touch optimized 44x44px minimum */}
        <motion.div
          variants={itemVariants}
        >
          <Button
            variant="default"
            size="lg"
            onClick={onNext}
            className="group min-h-[44px] px-8 sm:px-10 text-base sm:text-lg bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 shadow-lg shadow-blue-500/30 transition-all duration-300"
            data-testid="welcome-get-started"
          >
            {t('onboarding:get_started')}
            <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-200" />
          </Button>
        </motion.div>
      </motion.div>
    </div>
  );
}
